import * as THREE from "three";

/* ---------------- shared toon shaders for the earth/maze ---------------- */

export function createGroundToonMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: { lightDirection: { value: new THREE.Vector3(0, 1, 0).normalize() } },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying float vHeight;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vHeight = worldPos.y;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 lightDirection;
      varying vec3 vNormal;
      varying float vHeight;
      void main() {
        float light = dot(normalize(vNormal), normalize(lightDirection));
        light = clamp(light, -1.0, 1.0);
        light = mix(0.65, light, 0.30);
        vec3 colour;
        if (light < 0.30) colour = vec3(0.34, 0.49, 0.24);
        else if (light < 0.50) colour = vec3(0.49, 0.68, 0.37);
        else if (light < 0.72) colour = vec3(0.62, 0.81, 0.49);
        else colour = vec3(0.78, 0.93, 0.65);
        float heightTint = smoothstep(-2.0, 2.0, vHeight);
        vec3 lighter = colour * 1.05;
        vec3 darker = colour * 0.95;
        colour = mix(darker, lighter, heightTint);
        gl_FragColor = vec4(colour, 1.0);
      }`,
  });
}

/**
 * Toon material for the cloud shell (see clouds.js). Same fixed-light
 * banding trick as the ground/gate materials above - lightDirection is
 * compared directly against the view-space normal, so it reads as "lit
 * from the camera's up" rather than a fixed world direction. That's what
 * keeps the top of every puff looking bright no matter where the chase
 * camera currently is on the sphere.
 *
 * The vertex shader also does the cloud's whole "soft animation": each
 * puff carries a per-cloud aPhase attribute (baked in at build time) so a
 * gentle sine bob, along that vertex's own radial direction, comes for
 * free without any per-frame CPU work - clouds.js only has to advance
 * uTime and spin the shell slightly for wind drift.
 */
export function createCloudToonMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: {
      lightDirection: { value: new THREE.Vector3(0, 1, 0).normalize() },
      uTime: { value: 0 },
      uBobAmount: { value: 0.025 },
      uBobSpeed: { value: 0.55 },
    },
    vertexShader: /* glsl */ `
      attribute float aPhase;
      attribute float aTint;
      uniform float uTime;
      uniform float uBobAmount;
      uniform float uBobSpeed;
      varying vec3 vNormal;
      varying float vTint;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vTint = aTint;
        vec3 pos = position;
        vec3 radial = normalize(pos);
        pos += radial * (sin(uTime * uBobSpeed + aPhase) * uBobAmount);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 lightDirection;
      varying vec3 vNormal;
      varying float vTint;
      void main() {
        float light = dot(normalize(vNormal), normalize(lightDirection));
        light = clamp(light, -1.0, 1.0);
        light = mix(0.65, light, 0.30);
        vec3 colour;
        if (light < 0.30) colour = vec3(0.62, 0.66, 0.75);
        else if (light < 0.50) colour = vec3(0.80, 0.83, 0.89);
        else if (light < 0.72) colour = vec3(0.93, 0.94, 0.97);
        else colour = vec3(1.0, 1.0, 1.0);
        colour *= mix(0.96, 1.04, vTint);
        gl_FragColor = vec4(colour, 1.0);
      }`,
  });
}

export function createGateToonMaterial() {
  return new THREE.ShaderMaterial({
    uniforms: { lightDirection: { value: new THREE.Vector3(0, 1, 0).normalize() } },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying float vHeight;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 worldPos = modelMatrix * vec4(position, 1.0);
        vHeight = worldPos.y;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }`,
    fragmentShader: /* glsl */ `
      uniform vec3 lightDirection;
      varying vec3 vNormal;
      varying float vHeight;
      void main() {
        float light = dot(normalize(vNormal), normalize(lightDirection));
        light = clamp(light, -1.0, 1.0);
        light = mix(0.65, light, 0.30);
        vec3 colour;
        if (light < 0.30) colour = vec3(0.28, 0.26, 0.24);
        else if (light < 0.50) colour = vec3(0.45, 0.42, 0.38);
        else if (light < 0.72) colour = vec3(0.62, 0.59, 0.55);
        else colour = vec3(0.80, 0.77, 0.72);
        float heightTint = smoothstep(-2.0, 2.0, vHeight);
        vec3 lighter = colour * 1.04;
        vec3 darker = colour * 0.96;
        colour = mix(darker, lighter, heightTint);
        gl_FragColor = vec4(colour, 1.0);
      }`,
  });
}
